import cardTypeCheckers from 'types/cardTypeCheckers';
import isGauge from 'types/visTypeCheckers/isGauge';
import isPaceMatrix from 'types/visTypeCheckers/isPaceMatrix';
import isRankingMatrix from 'types/visTypeCheckers/isRankingMatrix';
import isSingleMetricDateMatrix from 'types/visTypeCheckers/isSingleMetricDateMatrix';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';

import { guessGaugeCard, reproducesGauge } from '../buildGaugeDefinition';
import {
  LegacyMatrix,
  guessLegacyMatrixCard,
  reproducesLegacyMatrix,
} from '../buildLegacyMatrixDefinition';
import {
  LegacyGadget,
  guessLegacyGadgetCard,
  isLegacyGadget,
  reproducesLegacyGadget,
} from '../legacyGadgetBlocks';
import { withCurrentVisType } from '../visTypes';
import { toKpiListPeriods } from '../kpiList';
import { guessSidecarCard, reproducesChart } from './chartMatching';
import {
  compositeForContentType,
  pickDateSettings,
  withoutUndefined,
} from './dateSettings';
import {
  getClassicReason,
  getConversion,
  getUnsupportedGadgetSettings,
  getDrawnChartDef,
  hasSameComparison,
} from './gadgetSupport';
import { readGadgetSettings } from './gadgetSettings';
import { readMetricList } from './metricListMatching';
import { guessRankingTableCard } from './rankingMatrix';
import {
  CardOrigin,
  CardOrigins,
  StoredGadget,
  TextCardContent,
} from './types';

const CARD_TYPE_LABELS: Partial<Record<CanvasCard.Content.Type, string>> = {
  'Chart Definition': 'Old chart reference',
  'Metric List': 'Metric list',
  Scorecard: 'KPI list',
  Goal: 'Goal',
};

// A sidecar as Studio now reads it: vis types it has since merged (e.g. a
// donut, now a Pie with a hole) as they are now
const currentSidecar = (
  studio: Studio.GadgetSidecar | undefined,
): Studio.GadgetSidecar | undefined =>
  studio && studio.version === 1
    ? { ...studio, card: withCurrentVisType(studio.card) }
    : studio;

const isLegacyMatrix = (
  chartDef: StoredGadget['gadget']['chartDef'],
): chartDef is LegacyMatrix =>
  isSingleMetricDateMatrix(chartDef) || isPaceMatrix(chartDef);

// Whether the settings rebuild a legacy matrix gadget and its comparison
const reproducesStoredMatrix = ({
  card,
  stored,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  stored: StoredGadget;
  chartDef: LegacyMatrix;
}): boolean =>
  reproducesLegacyMatrix({
    card,
    chartDef,
    comparison: stored.gadget.comparison,
    isReportGadget: stored.kind === 'report',
  });

// Whether the settings rebuild a Reminders, Group Vs Remainder or Entity
// Information Table gadget
const reproducesStoredLegacyGadget = ({
  card,
  stored,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  stored: StoredGadget;
  chartDef: LegacyGadget;
}): boolean =>
  reproducesLegacyGadget({
    card,
    chartDef,
    isReportGadget: stored.kind === 'report',
  });

// The Studio settings a gadget's chart can be edited with: the sidecar Studio
// saved, while it still rebuilds the stored chart and comparison (editing the
// gadget in the classic editor drops or outdates it), else settings that
// rebuild them exactly. Undefined when neither does.
const readChartSettings = (
  stored: StoredGadget,
): Studio.GadgetSidecarCard | undefined => {
  const { chartDef } = stored.gadget;
  const studio = currentSidecar(stored.gadget.studio);
  if (isGauge(chartDef)) {
    return studio &&
      studio.version === 1 &&
      reproducesGauge({ card: studio.card, chartDef })
      ? studio.card
      : guessGaugeCard(chartDef);
  }
  // Opened as a Table, and saved as one (a V5 matrix) once edited
  if (isRankingMatrix(chartDef)) {
    return guessRankingTableCard(chartDef);
  }
  // Date comparison and pace Tables, saved as they are
  if (isLegacyMatrix(chartDef)) {
    if (
      studio &&
      studio.version === 1 &&
      reproducesStoredMatrix({ card: studio.card, stored, chartDef })
    ) {
      return studio.card;
    }
    const guess = guessLegacyMatrixCard({
      chartDef,
      comparison: stored.gadget.comparison,
      isReportGadget: stored.kind === 'report',
    });
    return reproducesStoredMatrix({ card: guess, stored, chartDef })
      ? guess
      : undefined;
  }
  // Reminders and Entity Information Tables as Tables, Group Vs Remainder
  // as a Line, saved as they are
  if (isLegacyGadget(chartDef)) {
    if (
      studio &&
      studio.version === 1 &&
      reproducesStoredLegacyGadget({ card: studio.card, stored, chartDef })
    ) {
      return studio.card;
    }
    const guess = guessLegacyGadgetCard(chartDef);
    return reproducesStoredLegacyGadget({ card: guess, stored, chartDef })
      ? guess
      : undefined;
  }
  const drawn = getDrawnChartDef(stored);
  if (!isV5ChartDef(drawn)) {
    return undefined;
  }
  const reproduces = (card: Studio.GadgetSidecarCard) =>
    reproducesChart({ card, chartDef: drawn }) &&
    hasSameComparison({ card, stored });
  if (studio && studio.version === 1 && reproduces(studio.card)) {
    return studio.card;
  }
  const guess = guessSidecarCard({
    chartDef: drawn,
    comparison: stored.gadget.comparison,
    isReportGadget: stored.kind === 'report',
  });
  return guess && reproduces(guess) ? guess : undefined;
};

const readGadgetDates = (stored: StoredGadget): DateFiltered | undefined => {
  if (stored.kind === 'dashboard') {
    return pickDateSettings(stored.gadget);
  }
  const { dateField } = stored.gadget;
  return dateField ? { dateField } : undefined;
};

type BlockContent = Omit<Studio.Card, 'id' | 'layout'>;

// A dashboard gadget's run-time filters and popup, when it has them
const readDrillDowns = (
  gadget: DashboardGadget,
): Pick<Studio.Card, 'drillDowns' | 'reportDrillDownId' | 'boardDrillDownId'> =>
  withoutUndefined({
    drillDowns:
      gadget.drillDowns && gadget.drillDowns.length > 0
        ? gadget.drillDowns
        : undefined,
    reportDrillDownId: gadget.reportDrillDownId || undefined,
    boardDrillDownId: gadget.boardDrillDownId || undefined,
  });

// A block's content: its chart settings, with its gadget's dates, filters,
// run-time filters and popup, and gadget settings
const toBlockContent = ({
  stored,
  settings,
}: {
  stored: StoredGadget;
  settings: Studio.GadgetSidecarCard;
}): BlockContent => {
  const { scope } = stored.gadget;
  return withoutUndefined({
    ...settings,
    dateSettings: readGadgetDates(stored),
    filters: scope && scope.length > 0 ? scope : undefined,
    ...(stored.kind === 'dashboard' ? readDrillDowns(stored.gadget) : {}),
    gadgetSettings: readGadgetSettings(stored),
  });
};

const readGadget = (
  stored: StoredGadget,
): { content: BlockContent } | { reason: string } => {
  const settings =
    getUnsupportedGadgetSettings(stored).length === 0
      ? readChartSettings(stored)
      : undefined;
  if (!settings) {
    return { reason: getClassicReason(stored) };
  }
  return { content: toBlockContent({ stored, settings }) };
};

// A Classic block turned into a Studio block of its gadget, as far as
// Studio can draw it (see getConversion): the settings it can't are
// dropped. Its dates, filters and gadget settings come with it. It stays the
// same block, so on Save its gadget is written again under the same id.
// Undefined for a gadget Studio can't turn into a block.
export const convertClassicCard = ({
  card,
  stored,
}: {
  card: Studio.Card;
  // Its gadget as saved, or as edited in the legacy editor
  stored: StoredGadget;
}): Studio.Card | undefined => {
  const conversion = getConversion(stored);
  if (!conversion.isConvertible) {
    return undefined;
  }
  return {
    id: card.id,
    layout: card.layout,
    ...toBlockContent({ stored, settings: conversion.card }),
  };
};

// The Studio settings a gadget was saved with, while they still hold: Studio
// saved it, its sidecar still rebuilds the stored chart, and it has nothing
// Studio can't show. Only then do the dashboard and report views draw it as
// a Studio block; a gadget Studio merely could read (by guessing) keeps its
// classic look there. Named as its chart is: renaming it in the classic
// editor leaves the chart otherwise as Studio built it.
export const readStudioBlock = (
  stored: StoredGadget,
): Studio.GadgetSidecarCard | undefined => {
  const { chartDef } = stored.gadget;
  const studio = currentSidecar(stored.gadget.studio);
  if (
    !studio ||
    studio.version !== 1 ||
    getUnsupportedGadgetSettings(stored).length > 0
  ) {
    return undefined;
  }
  if (isGauge(chartDef)) {
    return reproducesGauge({ card: studio.card, chartDef })
      ? { ...studio.card, name: chartDef.name }
      : undefined;
  }
  if (isLegacyMatrix(chartDef)) {
    return reproducesStoredMatrix({ card: studio.card, stored, chartDef })
      ? { ...studio.card, name: chartDef.name }
      : undefined;
  }
  if (isLegacyGadget(chartDef)) {
    return reproducesStoredLegacyGadget({
      card: studio.card,
      stored,
      chartDef,
    })
      ? { ...studio.card, name: chartDef.name }
      : undefined;
  }
  const drawn = getDrawnChartDef(stored);
  if (!isV5ChartDef(drawn)) {
    return undefined;
  }
  return reproducesChart({ card: studio.card, chartDef: drawn }) &&
    hasSameComparison({ card: studio.card, stored })
    ? { ...studio.card, name: chartDef.name }
    : undefined;
};

// Whether Studio could open the gadget as one of its own blocks, e.g. once
// the legacy editor has removed what kept it Classic
export const isStudioReadable = (stored: StoredGadget): boolean =>
  'content' in readGadget(stored);

// Reads a saved dashboard's or report's canvas as Studio blocks. Cards Studio
// can't edit become Classic blocks that carry the card as it is. The origins
// remember where each block came from, for toCanvas.
const fromCanvas = ({
  contentType,
  canvas,
  dashboardGadgets,
  reportGadgets,
  textContents,
  metricLists = {},
  savedFilters = {},
  scorecards = {},
}: {
  contentType: Studio.ContentType;
  canvas: Canvas;
  dashboardGadgets: Record<string, DashboardGadget>;
  reportGadgets: Record<string, ReportGadgets.ReportGadget>;
  textContents: Record<string, TextCardContent>;
  // Metric List cards' lists, and the saved filters their items read
  metricLists?: Record<string, MetricListGadgetType | undefined>;
  savedFilters?: Record<string, LegacySavedFilter | undefined>;
  // Scorecard cards' KPI Lists (archived ones left out)
  scorecards?: Record<string, Scorecards.Scorecard | undefined>;
}): { cards: Studio.Card[]; origins: CardOrigins } => {
  const origins: CardOrigins = {};

  const cards = canvas.cards.map((canvasCard, index): Studio.Card => {
    const { i: id, x, y, w, h } = canvasCard.layout;
    const layout = { x, y, w, h };
    const origin: Omit<CardOrigin, 'card'> = { canvasCard, index };

    const read = ((): { content: BlockContent } | { reason: string } => {
      if (
        cardTypeCheckers.isDashboardGadget(canvasCard) ||
        cardTypeCheckers.isReportGadget(canvasCard)
      ) {
        const stored: StoredGadget | undefined =
          cardTypeCheckers.isDashboardGadget(canvasCard)
            ? dashboardGadgets[canvasCard.content.dashboardGadgetId] && {
                kind: 'dashboard',
                gadget: dashboardGadgets[canvasCard.content.dashboardGadgetId],
              }
            : reportGadgets[canvasCard.content.reportGadgetId] && {
                kind: 'report',
                gadget: reportGadgets[canvasCard.content.reportGadgetId],
              };
        if (!stored) {
          return { reason: 'Gadget not found' };
        }
        origin.gadget = stored;
        return readGadget(stored);
      }
      if (cardTypeCheckers.isText(canvasCard)) {
        const { textContentId } = canvasCard.content;
        const text = textContents[textContentId];
        if (!text) {
          return { reason: 'Text not found' };
        }
        origin.text = { id: textContentId, content: text };
        return {
          content: withoutUndefined({
            visType: 'text',
            text: text.textContent,
            isTextCentred: text.isCenterAlign || undefined,
            textFormat: text.format,
            textBackground: text.background,
            ...text.studio,
          }),
        };
      }
      // A metric list Table, saved as the list it is
      if (cardTypeCheckers.isMetricList(canvasCard)) {
        const list = metricLists[canvasCard.content.metricListId];
        if (!list) {
          return { reason: 'Metric list not found' };
        }
        origin.metricList = list;
        return readMetricList({ list, savedFilters });
      }
      // A KPI list Table, saved as the Scorecard card it is. The KPI List
      // itself is only ever edited on its own.
      if (cardTypeCheckers.isScorecard(canvasCard)) {
        const { scorecardId, numberOfPeriods } = canvasCard.content;
        if (!scorecards[scorecardId]) {
          return { reason: 'KPI list not found' };
        }
        const periods = toKpiListPeriods(numberOfPeriods);
        return {
          content: {
            visType: 'table',
            table: {
              rows: 'kpiList',
              kpiList:
                periods === undefined
                  ? { scorecardId }
                  : { scorecardId, numberOfPeriods: periods },
            },
          },
        };
      }
      // Media, held on the card itself
      if (cardTypeCheckers.isImage(canvasCard)) {
        const { imageSrc, imageLayout } = canvasCard.content;
        return {
          content: withoutUndefined({
            visType: 'image',
            mediaPath: imageSrc,
            imageLayout,
          }),
        };
      }
      if (cardTypeCheckers.isVideo(canvasCard)) {
        return {
          content: { visType: 'video', mediaPath: canvasCard.content.videoSrc },
        };
      }
      if (cardTypeCheckers.isIFrame(canvasCard)) {
        return {
          content: {
            visType: 'iframe',
            iFrameSnippet: canvasCard.content.iFrameSnippet,
          },
        };
      }
      if (cardTypeCheckers.isStudioComposite(canvasCard)) {
        return {
          content: {
            visType: 'composite',
            ...compositeForContentType({
              composite: canvasCard.content.composite,
              contentType,
            }),
          },
        };
      }
      const label =
        CARD_TYPE_LABELS[canvasCard.content.type] ?? canvasCard.content.type;
      return { reason: `Card type: ${label}` };
    })();

    const card: Studio.Card =
      'content' in read
        ? { id, ...read.content, layout }
        : {
            id,
            visType: 'classic',
            classic: { content: canvasCard.content, reason: read.reason },
            layout,
          };
    origins[id] = { ...origin, card };
    return card;
  });

  return { cards, origins };
};

export default fromCanvas;
